import {
  useModalContext,
  WSButton,
  WSCheckboxToggle,
  WSDateInput,
  WSElement,
  WSForm,
  WSModal,
  WSSelect,
  WSText,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import {
  IGrowthSourceNames,
  IMemberOccupation
} from "@wingspanhq/users/dist/lib/interfaces";
import { AxiosError } from "axios";
import React, { useEffect } from "react";
import * as Yup from "yup";
import { SetupFlowRouteComponentProps } from "../../../components/SetupFlow/SetupFlow";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { useWSMutation } from "../../../query/helpers";
import { useUserId } from "../../../query/hooks/helpers";
import {
  QUERY_USERS_ACTIVITIES,
  QUERY_USERS_MEMBER_PROFILE,
  QUERY_USERS_USER_PROFILE
} from "../../../query/users/keys";
import {
  useActivities,
  useMemberProfile,
  useOccupationsQuery,
  useUserProfile
} from "../../../query/users/queries";
import {
  getIsDomesticMember,
  getMemberCountryName,
  getPaymentsSetupProgress
} from "../../../query/users/selectors";
import { WSQueries } from "../../../query/WSQuery";
import { usersService } from "../../../services/users";
import {
  AddressField,
  addressToValues,
  AddressValues,
  addressValuesToRequest
} from "../../../Settings/components/AddressField/AddressField";
import { WS_LINKS } from "../../../types/wsLinks";
import { getChangedData } from "../../../utils/getChangedData";
import { openIntercom } from "../../../utils/intercom";
import { openInNewTab } from "../../../utils/openInNewTab";
import {
  textNumbersSymbolsStringValidator,
  validatorAddress,
  validatorDOB,
  validatorFirstName,
  validatorLastName,
  validatorSSN
} from "../../../utils/validators";
import { PaymentsSetupLayout } from "../../components/PaymentsSetupLayout/PaymentsSetupLayout";

type InvoicesSetupStep2FormValues = {
  firstName: string;
  lastName: string;
  preferredName: string;
  foreignTaxId: string;
  isUSPerson: boolean;
  ssn: string;
  isPersonalAddressTheSame: boolean;
  personalAddress: AddressValues;
  dob?: Date;
  mcc: string;
};

/**
 * We are hiding the Industry form field for enterprise sign-up and
 * setting the below mcc code of "Professional Services" as default industry.
 * Below value is taken from the occupations query.
 */
export const INDUSTRY_DEFAULT_VALUE = "8931"; // Professional Services

export const SetupPersonal: React.FC<SetupFlowRouteComponentProps> = ({
  onBack,
  onNext
}) => {
  const userId = useUserId();
  const userQuery = useUserProfile(userId);
  const memberQuery = useMemberProfile(userId);
  const activityQuery = useActivities(userId);
  const occupationsQuery = useOccupationsQuery();
  const { openModal, closeModal } = useModalContext();

  useEffect(() => {
    memberQuery.clear();
  }, []);

  const [submit, submitMeta] = useWSMutation<
    any,
    AxiosError,
    InvoicesSetupStep2FormValues
  >(
    async values => {
      const paymentsSetupProgress = getPaymentsSetupProgress(
        activityQuery.data
      );

      if (!memberQuery.data) throw new Error("Something went wrong");

      await usersService.user.update(userId, {
        profile: getChangedData(
          userQuery.data?.profile,
          {
            firstName: values.firstName,
            lastName: values.lastName,
            preferredName: values.preferredName,
            dob: values.dob
          },
          { dismissTimeInDates: true }
        )
      });

      const memberOccupations: IMemberOccupation[] = [];

      const matchingOccupation = (occupationsQuery.data || []).find(
        occupation => occupation.mccCode === values.mcc
      );

      if (matchingOccupation) {
        memberOccupations.push({
          taxCategory: matchingOccupation.category,
          category: matchingOccupation.category
        });
      }

      const isDomesticMember = getIsDomesticMember(memberQuery.data);

      await usersService.member.update(userId, {
        memberId: userId,
        profile: getChangedData(memberQuery.data.profile, {
          mcc: values.mcc,
          ssn: isDomesticMember || values.isUSPerson ? values.ssn : undefined,
          taxInfo: {
            occupations: memberOccupations
          },
          homeAddress: values.isPersonalAddressTheSame
            ? undefined
            : addressValuesToRequest(values.personalAddress),
          foreignTaxId: isDomesticMember ? undefined : values.foreignTaxId,
          foreignTaxCountry: isDomesticMember
            ? undefined
            : memberQuery.data.profile.address?.country
        })
      });

      await usersService.activity.update(userId, {
        flows: {
          paymentsSetup: {
            ...paymentsSetupProgress,
            currentStep: 3
          }
        }
      });

      return {
        isUSPerson: values.isUSPerson
      };
    },
    {
      dependencies: [
        QUERY_USERS_USER_PROFILE,
        QUERY_USERS_MEMBER_PROFILE,
        QUERY_USERS_ACTIVITIES
      ],
      onSuccess: onNext
    }
  );

  return (
    <PaymentsSetupLayout onBack={onBack} title="Verify personal details">
      <WSQueries
        queries={{
          userQuery,
          memberQuery,
          occupationsQuery,
          activityQuery
        }}
      >
        {({
          userQuery: { data: user },
          memberQuery: { data: member },
          occupationsQuery: { data: occupations },
          activityQuery: { data: activity }
        }) => {
          const isDomesticMember = getIsDomesticMember(member);
          const isEnterprise =
            activity.context?.growthSource === IGrowthSourceNames.Enterprise;
          const isCompany = member.profile.company?.taxId;

          return (
            <>
              <WSForm<InvoicesSetupStep2FormValues>
                defaultValues={{
                  firstName: user.profile.firstName,
                  lastName: user.profile.lastName,
                  preferredName: user.profile.preferredName,
                  isUSPerson: isDomesticMember || member.profile.ssnProvided,
                  isPersonalAddressTheSame: !member.profile.homeAddress
                    ?.country,
                  personalAddress: member.profile.homeAddress
                    ? addressToValues(member.profile.homeAddress)
                    : undefined,
                  dob: user.profile.dob,
                  ...(isEnterprise ? {} : { mcc: member.profile.mcc })
                }}
                validationSchema={Yup.object().shape({
                  firstName: isCompany
                    ? validatorFirstName
                    : validatorFirstName.required("Required"),
                  lastName: isCompany
                    ? validatorFirstName
                    : validatorLastName.required("Required"),
                  preferredName: textNumbersSymbolsStringValidator,
                  foreignTaxId: isDomesticMember
                    ? Yup.string()
                    : Yup.string().required("Required"),
                  ssn:
                    isCompany || member.profile.ssnProvided
                      ? Yup.string()
                      : Yup.string().when("isUSPerson", {
                          is: true,
                          then: validatorSSN.required("Required")
                        }),
                  personalAddress: isCompany
                    ? Yup.object().when("isPersonalAddressTheSame", {
                        is: true,
                        then: Yup.object(),
                        otherwise: validatorAddress
                      })
                    : Yup.object(),
                  dob: validatorDOB,
                  ...(isEnterprise
                    ? {}
                    : { mcc: Yup.string().required("Required") })
                })}
                onSubmit={values => {
                  submit(
                    isEnterprise
                      ? { ...values, mcc: INDUSTRY_DEFAULT_VALUE }
                      : values
                  );
                }}
              >
                <WSForm.Field
                  name="firstName"
                  label={
                    isCompany
                      ? "Legal first name (optional)"
                      : "Legal first name"
                  }
                  component={WSTextInput}
                  mb="M"
                />

                <WSForm.Field
                  name="lastName"
                  label={
                    isCompany ? "Legal last name (optional)" : "Legal last name"
                  }
                  component={WSTextInput}
                  mb="M"
                />

                <WSForm.Field
                  name="preferredName"
                  label="Preferred name (optional)"
                  component={WSTextInput}
                  mb="M"
                />

                <WSForm.Field
                  hidden={isDomesticMember}
                  name="isUSPerson"
                  component={WSCheckboxToggle}
                  componentProps={{
                    label: "I am a US person",
                    onInfo: () => {
                      openModal("WHAT_IS_US_PERSON");
                    }
                  }}
                  mb="M"
                />

                <WSForm.Value name="isUSPerson">
                  {isUSPerson => (
                    <WSElement
                      hidden={
                        (!isUSPerson && !isDomesticMember) ||
                        member.profile.ssnProvided
                      }
                    >
                      <WSForm.Field
                        name="ssn"
                        label={isCompany ? "SSN (optional)" : "SSN"}
                        component={WSTextInput}
                        componentProps={{
                          type: "password",
                          allowToShowPassword: true,
                          formatOptions: {
                            numericOnly: true,
                            blocks: [9]
                          }
                        }}
                        mb="XS"
                      />

                      <WSButton.Link
                        mb="M"
                        type="button"
                        onClick={() => {
                          openModal("SSN_DESCRIPTION_MODAL");
                        }}
                      >
                        What is this used for?
                      </WSButton.Link>
                    </WSElement>
                  )}
                </WSForm.Value>

                <WSForm.Field
                  hidden={isDomesticMember}
                  mb="M"
                  name="foreignTaxId"
                  label={`${getMemberCountryName(member)} Tax ID`}
                  component={WSTextInput}
                />

                <WSForm.Field
                  hidden={!isCompany}
                  name="isPersonalAddressTheSame"
                  component={WSCheckboxToggle}
                  componentProps={{
                    label:
                      "My personal address is the same as my business address"
                  }}
                  mb="M"
                />

                <WSForm.Value name="isPersonalAddressTheSame">
                  {isPersonalAddressTheSame => (
                    <>
                      <AddressField
                        mb="L"
                        name="personalAddress"
                        hidden={!isCompany || isPersonalAddressTheSame}
                      />
                    </>
                  )}
                </WSForm.Value>

                <WSForm.Field
                  name="dob"
                  label="Birthday"
                  component={WSDateInput}
                  componentProps={{
                    manualDateEntry: true
                  }}
                  mb="M"
                />

                {isEnterprise ? null : (
                  <WSForm.Field
                    name="mcc"
                    label="Industry"
                    component={WSSelect}
                    componentProps={{
                      options: occupations.map(occupation => ({
                        value: occupation.mccCode,
                        label: occupation.category
                      }))
                    }}
                    mb="M"
                  />
                )}

                <WSErrorMessage
                  mb="M"
                  contextKey="PaymentsSetup"
                  error={submitMeta.error}
                />

                <WSButton
                  mt="L"
                  fullWidth
                  loading={submitMeta.isLoading}
                  name="next"
                >
                  Verify personal details
                </WSButton>
              </WSForm>

              <WSModal
                name="SSN_DESCRIPTION_MODAL"
                size="S"
                title="Why does Wingspan ask for your SSN?"
              >
                <WSText mb="S">
                  This information is requested as part of Wingspan’s identity
                  verification requirements. Wingspan collects, verifies, and
                  maintains information on the individuals associated with every
                  Wingspan account, as required by our regulators, customers and
                  partners.
                </WSText>
                <WSText mb="S">
                  Our Privacy Policy further explains how and why we collect,
                  use, retain, disclose, and safeguard any personal data you
                  provide to us.
                </WSText>
                {!member.profile.company?.taxId && (
                  <WSText mb="S">
                    If you would prefer to provide an EIN instead of an SSN,
                    please{" "}
                    <WSButton.Link
                      inline
                      onClick={() => {
                        closeModal("SSN_DESCRIPTION_MODAL");
                        onBack();
                      }}
                    >
                      update your business information
                    </WSButton.Link>{" "}
                    and provide an EIN.
                  </WSText>
                )}
                <WSText mb="M">
                  Have a question?{" "}
                  <WSButton.Link inline onClick={openIntercom}>
                    Contact support
                  </WSButton.Link>
                </WSText>
              </WSModal>

              <WSModal
                name="WHAT_IS_US_PERSON"
                size="S"
                title="What is a United States person?"
              >
                <WSText mb="S">
                  A US persons is a legal classification of a taxpayer for tax
                  purposes. If you’re a citizen or resident of the United
                  States, you are a US persons.
                </WSText>
                <WSButton.Link
                  onClick={() => {
                    openInNewTab(WS_LINKS.taxpayersClassification);
                  }}
                  type="button"
                >
                  Learn more
                </WSButton.Link>
              </WSModal>
            </>
          );
        }}
      </WSQueries>
    </PaymentsSetupLayout>
  );
};
